import { HEALTH_RECORDS_PAGES } from 'lib/constants/healthRecords';
import { getDownloadHeadersWithCurrentAccountConsumerId } from 'lib/downloads/utils';
import {
  Action,
  DFDHeadersObject,
  DispatcherResource,
  DispatcherEndpoint
} from 'modules/types/common';
import { convertToLowerKabobCase } from 'modules/utils/StringUtils';
import Config from 'react-native-config';
import { Dispatch } from 'redux';
import FileServices from 'services/FileServices';
import { CacheTypes } from 'modules/types/mobileCaching';
import {
  immunizationsDownloadingSelector,
  immunizationsRequestStateSelector
} from 'store/medicalHistory/immunizations/selectors';
import { messageReply, messageShare } from 'store/messaging/actions';
import { currentConsumerPcpDoctorsSelector } from 'store/myDoctors/selectors';
import { RootDispatch, RootState, Thunk } from 'store/types';
import { DFDHeaders } from 'serverTypes/authentication';
import { oc } from 'ts-optchain';
import { currentAccountConsumerFullNameSelector } from 'store/account/selectors';
import { getMedicalHistoryEndpointGenerator } from '../common';
import { TransmitSources } from '../../transmit';
import { Immunization } from '../types';

/**
 * Get Immunizations
 */
export const FETCH_IMMUNIZATIONS_CACHE_SUCCESS = 'IMMUNIZATIONS.FETCH_IMMUNIZATIONS_CACHE_SUCCESS';

export const FETCH_IMMUNIZATIONS = 'IMMUNIZATIONS.FETCH_IMMUNIZATIONS';
export const FETCH_IMMUNIZATIONS_SUCCESS = 'IMMUNIZATIONS.FETCH_IMMUNIZATIONS_SUCCESS';
export const FETCH_IMMUNIZATIONS_FAIL = 'IMMUNIZATIONS.FETCH_IMMUNIZATIONS_FAIL';

const getEndpoint = getMedicalHistoryEndpointGenerator(DispatcherResource.IMMUNIZATIONS);

export const fetchImmunizations = (useRequestState = true) => ({
  type: FETCH_IMMUNIZATIONS,
  payload: {
    useRequestState,
    requestStateSelector: immunizationsRequestStateSelector,
    cacheType: CacheTypes.IMMUNIZATIONS,
    cacheRetrievalSuccessAction: FETCH_IMMUNIZATIONS_CACHE_SUCCESS,
    client: 'dispatcher',
    request: {
      method: 'get',
      url: getEndpoint()
    }
  }
});

/**
 * Get Utah School Immunization Record
 */
export const FETCH_UTAH_SCHOOL_IMMUNIZATION_RECORD =
  'IMMUNIZATIONS.FETCH_UTAH_SCHOOL_IMMUNIZATION_RECORD';
export const FETCH_UTAH_SCHOOL_IMMUNIZATION_RECORD_SUCCESS =
  'IMMUNIZATIONS.FETCH_UTAH_SCHOOL_IMMUNIZATION_RECORD_SUCCESS';
export const FETCH_UTAH_SCHOOL_IMMUNIZATION_RECORD_FAIL =
  'IMMUNIZATIONS.FETCH_UTAH_SCHOOL_IMMUNIZATION_RECORD_FAIL';

export const fetchUSIR = () => {
  const request = {
    method: 'get',
    url: getEndpoint(DispatcherEndpoint.SIR)
  };

  if (Config.NODE_ENV === 'production') {
    return {
      type: FETCH_UTAH_SCHOOL_IMMUNIZATION_RECORD,
      payload: {
        client: 'dispatcher',
        request
      }
    };
  }

  return {
    type: FETCH_UTAH_SCHOOL_IMMUNIZATION_RECORD,
    payload: {
      client: 'dispatcher',
      request: { ...request, timeout: 60 * 1000 * 2 }
    }
  };
};

/**
 * Get Utah School Immunization Record exists
 */
export const FETCH_UTAH_SCHOOL_IMMUNIZATION_RECORD_EXISTS =
  'IMMUNIZATIONS.FETCH_UTAH_SCHOOL_IMMUNIZATION_RECORD_EXISTS';
export const FETCH_UTAH_SCHOOL_IMMUNIZATION_RECORD_EXISTS_SUCCESS =
  'IMMUNIZATIONS.FETCH_UTAH_SCHOOL_IMMUNIZATION_RECORD_EXISTS_SUCCESS';
export const FETCH_UTAH_SCHOOL_IMMUNIZATION_RECORD_EXISTS_FAIL =
  'IMMUNIZATIONS.FETCH_UTAH_SCHOOL_IMMUNIZATION_RECORD_EXISTS_FAIL';

export const fetchHasUSIR = () => {
  const request = {
    method: 'get',
    url: getEndpoint(DispatcherEndpoint.SIR_EXISTS)
  };

  if (Config.NODE_ENV === 'production') {
    return {
      type: FETCH_UTAH_SCHOOL_IMMUNIZATION_RECORD_EXISTS,
      payload: {
        client: 'dispatcher',
        request
      }
    };
  }

  return {
    type: FETCH_UTAH_SCHOOL_IMMUNIZATION_RECORD_EXISTS,
    payload: {
      client: 'dispatcher',
      request: { ...request, timeout: 60 * 1000 * 2 }
    }
  };
};

export const replyToImmunizations = (
  immunizations: Immunization[],
  isShare = false,
  returnBackTo?: string
): Thunk => (dispatch, getState) => {
  const consumerPcpList = currentConsumerPcpDoctorsSelector(getState());

  if (!immunizations) {
    return;
  }

  const params = {
    subject: 'Medical History',
    readonly: immunizations
      .map(
        im =>
          `\nReceived ${im.immunizationName} from ${im.source}\n(logged on ${im.immunizationDate} with id: ${im.immunizationId})`
      )
      .join(`,\n`),
    transmitParams: {
      source: TransmitSources.Immunizations,
      payload: immunizations,
      returnBackTo
    }
  };

  if (isShare) {
    dispatch(messageShare(params));
  } else {
    dispatch(messageReply(params, oc(consumerPcpList)[0].cernerPersonnelId('')));
  }
};

export const SHARE_IMMUNIZATIONS = 'IMMUNIZATIONS.SHARE_IMMUNIZATIONS';
export const SHARE_IMMUNIZATIONS_SUCCESS = 'IMMUNIZATIONS.SHARE_IMMUNIZATIONS_SUCCESS';
export const SHARE_IMMUNIZATIONS_FAIL = 'IMMUNIZATIONS.SHARE_IMMUNIZATIONS_FAIL';

export const shareImmunizations = (
  immunizations: Immunization[],
  toAddress: string,
  message: string,
  useSecureEmail: boolean,
  contactPhoneNumber: string,
  subject: string
) => ({
  type: SHARE_IMMUNIZATIONS,
  payload: {
    client: 'dispatcher',
    request: {
      method: 'post',
      url: getEndpoint(DispatcherEndpoint.TRANSMIT),
      data: {
        toAddress,
        message,
        useSecureEmail,
        contactPhoneNumber,
        subject
      }
    }
  }
});

/**
 * Download Immunizations
 */
export const DOWNLOAD_IMMUNIZATIONS = 'IMMUNIZATIONS.DOWNLOAD_IMMUNIZATIONS';
export const DOWNLOAD_IMMUNIZATIONS_SUCCESS = 'IMMUNIZATIONS.DOWNLOAD_IMMUNIZATIONS_SUCCESS';
export const DOWNLOAD_IMMUNIZATIONS_FAIL = 'IMMUNIZATIONS.DOWNLOAD_IMMUNIZATIONS_FAIL';

export const downloadImmunizations = () => async (
  dispatch: Dispatch,
  getState: () => RootState
) => {
  const state = getState();
  // Check for in flight downloads if there are any then return
  if (immunizationsDownloadingSelector(state) === true) {
    return;
  }
  dispatch({ type: DOWNLOAD_IMMUNIZATIONS, payload: {} });

  const timestamp = Date.now();

  try {
    const uri = getEndpoint(DispatcherEndpoint.DOWNLOAD, true);

    const headers: DFDHeadersObject = {
      [DFDHeaders.mhpAccept]: 'application/pdf',
      ...getDownloadHeadersWithCurrentAccountConsumerId(state, uri)
    };
    const response = await FileServices.download(`immunizations-${timestamp}.pdf`, uri, headers);

    dispatch({ type: DOWNLOAD_IMMUNIZATIONS_SUCCESS, payload: { response } });
  } catch (err) {
    dispatch({ type: DOWNLOAD_IMMUNIZATIONS_FAIL, error: err });
  }
};

export const shareUtahSchoolImmunizationRecord = (base64: string, returnBackTo?: string): Thunk => (
  dispatch: Dispatch,
  getState: () => RootState
) => {
  if (!base64.length) {
    return;
  }

  const state = getState();
  const profileName = currentAccountConsumerFullNameSelector(state);

  const params = {
    subject: HEALTH_RECORDS_PAGES.MedicalHistory.usirButton,
    message: `Utah school immunization record for ${profileName}`,
    attachments: [{ base64, filename: 'utah_school_immunization_record.pdf' }],
    transmitParams: {
      source: TransmitSources.UtahSchoolImmunizationRecord,
      returnBackTo
    }
  };

  dispatch(messageShare(params));
};

export const SHARE_USIR = 'IMMUNIZATIONS.SHARE_USIR';
export const SHARE_USIR_SUCCESS = 'IMMUNIZATIONS.SHARE_USIR_SUCCESS';
export const SHARE_USIR_FAIL = 'IMMUNIZATIONS.SHARE_USIR_FAIL';

export const shareWithExternalUtahSchoolImmunizationRecord = (
  payload: string,
  toAddress: string,
  message: string,
  useSecureEmail: boolean,
  contactPhoneNumber: string,
  subject: string
): Action => ({
  type: SHARE_USIR,
  payload: {
    client: 'dispatcher',
    request: {
      method: 'post',
      url: getEndpoint(DispatcherEndpoint.SIR_TRANSMIT),
      data: {
        toAddress,
        message,
        useSecureEmail,
        contactPhoneNumber,
        subject
      }
    }
  }
});

export const downloadUSIR = (data: string) => async (
  dispatch: RootDispatch,
  getState: () => RootState
) => {
  const state = getState();
  const profileName = currentAccountConsumerFullNameSelector(state);
  const nameAsKabab = convertToLowerKabobCase(profileName);
  await FileServices.saveDataToFile(`${nameAsKabab}-utah-school-immunization-record.pdf`, data);
  return { type: 'USIR_DOWNLOAD_SUCCESS' };
};
